import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import axios from 'axios';
import {
  DocumentCheckIcon,
  ClockIcon,
  ShieldCheckIcon,
  MapPinIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  CheckCircleIcon,
  StarIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  TrophyIcon,
  GlobeAltIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';

// Icon mapping
const iconMap = {
  DocumentCheckIcon,
  ClockIcon,
  ShieldCheckIcon,
  MapPinIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  CheckCircleIcon,
  StarIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  TrophyIcon,
  GlobeAltIcon,
  BanknotesIcon
};

const WhyChooseSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [features, setFeatures] = useState([]);
  const [stats, setStats] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch dynamic data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuresRes, statsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/whychoose/features/active'),
          axios.get('/api/admin/whychoose/stats/active'),
          axios.get('/api/admin/whychoose/settings')
        ]);

        // Map features with icon components
        const featuresData = (featuresRes.data.data || []).map(feature => ({
          ...feature,
          icon: iconMap[feature.icon_name] || CheckCircleIcon,
          color: `text-${feature.color_class}-600`,
          bgColor: feature.bg_color_class || 'bg-green-50'
        }));

        // Map stats with icon components
        const statsData = (statsRes.data.data || []).map(stat => ({
          ...stat,
          icon: iconMap[stat.icon_name] || UsersIcon
        }));

        setFeatures(featuresData);
        setStats(statsData);
        setSettings(settingsRes.data.data || {
          section_title: t('whyChoose.title'),
          section_subtitle: t('whyChoose.subtitle'),
          cta_text: 'Get Started',
          cta_link: '/contact'
        });
      } catch (error) {
        console.error('Error fetching WhyChoose data:', error);
        // Fallback to empty arrays
        setFeatures([]);
        setStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Determine content based on language
  const isTamil = i18n.language === 'ta';

  // Static Tamil Data based on user requirements
  const tamilFeatures = [
    {
      id: 'lowDoc',
      title: t('whyChoose.features.lowDoc.title'),
      description: t('whyChoose.features.lowDoc.description'),
      icon: DocumentCheckIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'fastProcessing',
      title: t('whyChoose.features.fastProcessing.title'),
      description: t('whyChoose.features.fastProcessing.description'),
      icon: ClockIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      id: 'customerTrust',
      title: t('whyChoose.features.customerTrust.title'),
      description: t('whyChoose.features.customerTrust.description'),
      icon: ShieldCheckIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'regionalExpertise',
      title: t('whyChoose.features.regionalExpertise.title'),
      description: t('whyChoose.features.regionalExpertise.description'),
      icon: MapPinIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const tamilStats = [
    { id: 'customers', value: 1499, label: t('whyChoose.stats.customers'), suffix: '+', icon: UsersIcon, color_class: 'text-blue-600' },
    { id: 'branches', value: 34, label: t('whyChoose.stats.branches'), suffix: '+', icon: BuildingOfficeIcon, color_class: 'text-red-600' },
    { id: 'experience', value: 5, label: t('whyChoose.stats.experience'), suffix: '+', icon: CalendarIcon, color_class: 'text-yellow-600' },
    { id: 'disbursed', value: 250, label: t('whyChoose.stats.disbursed'), suffix: '+ கோடி', icon: CurrencyRupeeIcon, color_class: 'text-green-600' }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  const displayFeatures = isTamil ? tamilFeatures : features;
  const displayStats = isTamil ? tamilStats : stats;
  const displaySettings = isTamil ? {
    section_title: t('whyChoose.title'),
    section_subtitle: t('whyChoose.subtitle'),
    cta_text: t('whyChoose.stats.cta'),
    cta_link: '/contact'
  } : settings;

  return (
    <section className="py-20 bg-gradient-light relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, #283079 0%, transparent 50%)`
        }} />
      </div>

      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {displaySettings?.section_title || t('whyChoose.title')}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {displaySettings?.section_subtitle || t('whyChoose.subtitle')}
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        {loading && !isTamil ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="w-16 h-16 bg-gray-200 rounded-2xl mx-auto mb-6"></div>
                <div className="h-6 bg-gray-200 rounded mb-3"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : displayFeatures.length > 0 ? (
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
          >
            {displayFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                variants={itemVariants}
                className="group"
              >
                <div className="card card-hover text-center h-full">
                  <div className={`${feature.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className={`h-8 w-8 ${feature.color}`} />
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : null}

        {/* Statistics Section */}
        {isTamil && (
          <motion.div
            variants={containerVariants}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-bold text-gray-900">{t('whyChoose.stats.title')}</h2>
          </motion.div>
        )}

        {loading && !isTamil ? (
          <div className="bg-white rounded-3xl shadow-large p-8 lg:p-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="text-center animate-pulse">
                  <div className="w-16 h-16 bg-gray-200 rounded-2xl mx-auto mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                </div>
              ))}
            </div>
          </div>
        ) : displayStats.length > 0 ? (
          <motion.div
            variants={containerVariants}
            className="bg-white rounded-3xl shadow-large p-8 lg:p-12"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {displayStats.map((stat, index) => (
                <motion.div
                  key={stat.id}
                  variants={itemVariants}
                  className="text-center group"
                >
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${stat.color_class} bg-gray-50 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon className="h-8 w-8" />
                  </div>

                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {inView && (
                      <CountUp
                        start={0}
                        end={stat.value}
                        duration={2.5}
                        delay={index * 0.2}
                        separator=","
                      />
                    )}
                    <span className={stat.color_class}>{stat.suffix}</span>
                  </div>

                  <p className="text-gray-600 font-medium">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : null}

        {/* Bottom CTA */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <motion.a
            href={displaySettings?.cta_link || '/contact'}
            className="btn-primary bg-[#ed2636] text-lg px-8 py-4 inline-block"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {displaySettings?.cta_text || t('common.getStarted')}
          </motion.a>
        </motion.div>


      </div>
    </section>
  );
};

export default WhyChooseSection;